import {InfiniteData, useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {communicationApi} from '../api';
import {CommunicationMessagesFilter, CommunicationMessagesWithMetaModel} from '../model';
import {useCallback, useMemo, useState} from 'react';

type CommunicationMessagesState = {
  getInfiniteMessages: UseInfiniteQueryResult<InfiniteData<CommunicationMessagesWithMetaModel>, Error>;
  filters: CommunicationMessagesFilter;
  updateFilter: (newFilter: Partial<CommunicationMessagesFilter>) => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useCommunicationMessages = (
  initialFilter: CommunicationMessagesFilter = {},
): CommunicationMessagesState => {
  const [filters, setFilters] = useState<CommunicationMessagesFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const infiniteQueryFilters = useMemo(() => {
    const {after, pageSize, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteMessages = useInfiniteQuery<
    CommunicationMessagesWithMetaModel,
    Error,
    InfiniteData<CommunicationMessagesWithMetaModel>,
    ['communication-messages', 'infinite', Omit<CommunicationMessagesFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['communication-messages', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return communicationApi.getMessages({...infiniteQueryFilters, first: filters.pageSize, after: pageParam});
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<CommunicationMessagesFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  return {
    getInfiniteMessages,
    filters,
    updateFilter,
  };
};
